import { Component, OnInit } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { currency } from 'app/constants/currency.constants';
import { CalculationService, DateService, HelperService, HTTPService } from 'app/services';
import { datetimeFormat } from 'app/constants/date-time-format';

@Component({
  selector: 'app-edit-commission-details',
  templateUrl: './edit-commission-details.component.html',
  styleUrls: ['./edit-commission-details.component.scss'],
})
export class EditCommissionDetailsComponent implements OnInit {
  serviceId: string = '';
  orderId: string = '';
  orderCode: string = '';
  employeeId: string = '';
  employeeRole: string = '';
  employeeName: string = '';
  usd: string = currency.USD;
  stats: any[] = [];
  invoiceMetrics: any[] = [];
  serviceMetrics: any[] = [];
  activityLogs: any[] = [];
  isSyncing: boolean = false;
  isProcessing: boolean = false;
  invoiceCommissions: any[] = [];
  laborCommissions: any[] = [];
  nonLaborCommissions: any[] = [];
  totalCommission: number = 0;
  totalInvoiceCommission: number = 0;
  totalLaborCommission: number = 0;
  totalNonLaborCommission: number = 0;
  commissionChange: any = {};
  commissionInputFocus: any = {};
  isAllPlanLogs: boolean = false;

  constructor(
    private httpService: HTTPService,
    private helperService: HelperService,
    private calculationService: CalculationService,
    private currencyPipe: CurrencyPipe,
    private dialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    public dateService: DateService,
  ) {}

  ngOnInit() {
    this.serviceId = this.dialogConfig.data.serviceId;
    this.orderId = this.dialogConfig.data.orderId;
    this.orderCode = this.dialogConfig.data.orderCode;
    this.employeeId = this.dialogConfig.data.employeeId;
    this.employeeRole = this.dialogConfig.data.employeeRole;
    this.employeeName = this.dialogConfig.data.employeeName;

    if (this.orderId) {
      this.getCommissionDetails();
    }
  }

  getCommissionDetails() {
    this.httpService.getCommissionDetails(this.orderId, this.employeeId, this.serviceId, this.employeeRole).then(
      (data: any) => {
        const { activityLogs, commissionDetails } = data;

        this.totalInvoiceCommission = 0;
        this.totalLaborCommission = 0;
        this.totalNonLaborCommission = 0;
        this.invoiceCommissions = [];
        this.laborCommissions = [];
        this.nonLaborCommissions = [];
        this.activityLogs = activityLogs;

        commissionDetails.forEach((commissionDetail: any) => {
          const { service, item, commissionEarned, actualCommissionEarned } = commissionDetail;

          commissionDetail.commissionTooltip = `\
                <div class='flex-column'>
                  <span>Calculated commission: ${this.currencyPipe.transform(actualCommissionEarned || 0, this.usd)}</span>
                  <span>Click to revert to it</span>
                </div>`;

          if (item && item.orderItemId) {
            commissionDetail.tooltip = this.makeTooltip(commissionDetail);

            if (item.type === 'labor') {
              this.laborCommissions.push(commissionDetail);
            } else {
              this.nonLaborCommissions.push(commissionDetail);
            }

            this.commissionChange[`${service.orderServiceId}-${item.orderItemId}`] = commissionEarned;
          } else {
            this.invoiceCommissions.push(commissionDetail);
            this.commissionChange['invoice-metric'] = commissionEarned;
          }
        });

        this.calculateCommissionTotal(false);
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  syncCommission() {
    if (this.isSyncing) return;

    this.isSyncing = true;
    this.httpService
      .syncCommissionDetails(this.orderId)
      .then(
        () => {
          this.getCommissionDetails();
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      )
      .finally(() => {
        this.isSyncing = false;
      });
  }

  handleChange(serviceId: string, itemId: string, value: any) {
    this.commissionChange[`${serviceId}-${itemId}`] = value;
  }

  handleFocus(serviceId: string, itemId: string) {
    this.commissionInputFocus[`${serviceId}-${itemId}`] = true;
  }

  handleBlur(serviceId: string, itemId: string) {
    this.commissionInputFocus[`${serviceId}-${itemId}`] = false;
    this.calculateCommissionTotal();
  }

  handleUndoCommission(serviceId: string, itemId: string, actualCommissionEarned: number) {
    this.commissionChange[`${serviceId}-${itemId}`] = actualCommissionEarned;
    this.calculateCommissionTotal();
  }

  isCommissionUpdate(currentCommission: any, actualCommission: any) {
    return Number(currentCommission) !== Number(actualCommission);
  }

  calculateCommissionTotal(useLatestValues: boolean = true) {
    if (useLatestValues) {
      if (this.invoiceCommissions.length) {
        this.totalInvoiceCommission = this.calculationService.roundOff(this.invoiceCommissions.map(() => Number(this.commissionChange[`invoice-metric`])).reduce((value = 0, acc) => value + acc));
      }

      if (this.laborCommissions.length) {
        this.totalLaborCommission = this.calculationService.roundOff(
          this.laborCommissions.map(({ service, item }) => Number(this.commissionChange[`${service.orderServiceId}-${item.orderItemId}`])).reduce((value = 0, acc) => value + acc)
        );
      }

      if (this.nonLaborCommissions.length) {
        this.totalNonLaborCommission = this.calculationService.roundOff(
          this.nonLaborCommissions.map(({ service, item }) => Number(this.commissionChange[`${service.orderServiceId}-${item.orderItemId}`])).reduce((value = 0, acc) => value + acc)
        );
      }
    } else {
      if (this.invoiceCommissions.length) {
        this.totalInvoiceCommission = this.calculationService.roundOff(this.invoiceCommissions.map(({ commissionEarned }) => commissionEarned).reduce((value = 0, acc) => value + acc));
      }

      if (this.laborCommissions.length) {
        this.totalLaborCommission = this.calculationService.roundOff(this.laborCommissions.map(({ commissionEarned }) => commissionEarned).reduce((value = 0, acc) => value + acc));
      }

      if (this.nonLaborCommissions.length) {
        this.totalNonLaborCommission = this.calculationService.roundOff(this.nonLaborCommissions.map(({ commissionEarned }) => commissionEarned).reduce((value = 0, acc) => value + acc));
      }
    }

    this.totalCommission = this.calculationService.roundOff(this.totalInvoiceCommission + this.totalLaborCommission + this.totalNonLaborCommission);
  }

  makeTooltip({ item, commission, commissionableAmount }) {
    const lines = [];
    const appliesOn = item.type === 'labor' ? commission.serviceMetricType.labor.appliesOn : commission.serviceMetricType.nonLabor.appliesOn;

    lines.push(`<div class="flex-between-center width-100p">
                  <span>Item amount</span>
                  <span>${this.currencyPipe.transform(item.subTotal, this.usd)}</span>
                </div>`);

    if (item.itemLevelDiscount) {
      lines.push(`<div class="flex-between-center width-100p">
                    <span>Line discount</span>
                    <span>(${this.currencyPipe.transform(item.itemLevelDiscount, this.usd)})</span>
                  </div>`);
    }

    if (item.serviceLevelDiscount) {
      lines.push(`<div class="flex-between-center width-100p">
                    <span>Allocated service discount</span
                    <span>(${this.currencyPipe.transform(item.serviceLevelDiscount, this.usd)})</span>
                  </div>`);
    }

    if (item.orderLevelDiscount) {
      lines.push(`<div class="flex-between-center width-100p">
                    <span>Allocated order discount</span
                    <span>(${this.currencyPipe.transform(item.orderLevelDiscount, this.usd)})</span>
                  </div>`);
    }

    if (appliesOn === 'grossProfit') {
      lines.push(`<div class="flex-between-center width-100p">
                      <span>Cost</span
                      <span>(${this.currencyPipe.transform(item.totalCost, this.usd)})</span>
                    </div>`);
    }

    lines.push(`<div class="flex-between-center width-100p border-top">
                  <span>Commissionable amount</span>
                  <span>${this.currencyPipe.transform(commissionableAmount, this.usd)}</span>
                </div>`);

    return `<div class="flex-column">${lines.join('')}</div>`;
  }

  formatPayload() {
    const payloads = [];

    [...this.invoiceCommissions, ...this.laborCommissions, ...this.nonLaborCommissions].forEach(({ _id, service, item, commissionEarned, actualCommissionEarned }) => {
      let currentCommission = this.commissionChange[`${service && service.orderServiceId}-${item && item.orderItemId}`];

      if (item && item.orderItemId) {
        currentCommission = this.commissionChange[`${service && service.orderServiceId}-${item && item.orderItemId}`];
      } else {
        currentCommission = this.commissionChange['invoice-metric'];
      }

      if (Number(currentCommission) !== Number(commissionEarned)) {
        const payload: any = {
          _id,
          actionType: '',
          commissionEarned: currentCommission,
          previousCommissionEarned: commissionEarned,
          fCommissionEarned: this.currencyPipe.transform(currentCommission, this.usd),
          fPreviousCommissionEarned: this.currencyPipe.transform(commissionEarned, this.usd),
        };

        if (item) {
          payload.itemId = item.orderItemId;
          payload.itemType = item.type;
          payload.itemTitle = item.title;
        }

        if (service) {
          payload.serviceId = service.orderServiceId;
        }

        if (Number(currentCommission) === actualCommissionEarned) {
          payload.actionType = 'revert';
        } else {
          payload.actionType = 'update';
        }

        payloads.push(payload);
      }
    });

    return payloads;
  }

  handleClose(isSuccess: boolean = false, data: any = null) {
    this.dynamicDialogRef.close({ isSuccess, data });
  }

  handleSubmit() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    this.httpService
      .updateCommissionDetails(this.employeeId, this.orderId, this.formatPayload())
      .then(
        () => {
          this.handleClose(true);
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      )
      .finally(() => {
        this.isProcessing = false;
      });
  }

   getTime(time) {
      return this.dateService.formatDate(this.dateService.getCurrentDateTimeTz(new Date(time).getTime()), datetimeFormat.mmmdyyhhmmaa);
    }
}
